// ============================================================
// connection.ts — WebSocket + WebRTC DataChannel connection manager
// with reconnect, message queuing, ping tracking, and quality stats.
// ============================================================

import { saveToken, getToken, clearToken } from '../auth/token-store';
import type { GameClock } from '../core/clock';

export interface ServerMessage {
  // F-NC-012: union mirrors every top-level `"type": "<x>"` envelope the
  // server emits in server/internal/net/*.go. Nested event types
  // (`team_full`, `chat`, `team_switch_blocked`, etc.) ride inside the
  // `event` envelope's `data.type` field — they aren't top-level. Add a
  // vitest snapshot test if you bump this so the contract drift is caught
  // at CI time rather than at runtime.
  type:
    | 'snapshot' | 'delta_snapshot' | 'event'
    | 'welcome' | 'error' | 'pong'
    | 'webrtc_offer' | 'webrtc_answer' | 'webrtc_ice'
    | 'voice_speaking'
    | 'world_state_rehydrate';
  data: any;
}

export interface ClientMessage {
  // F-NC-011: union mirrors every `case "<type>":` arm in
  // server/internal/net/websocket.go's readLoop plus the WebRTC signaling
  // entries from webrtc.go. Add a vitest snapshot test if you bump this so
  // the contract drift is caught at CI time rather than at runtime.
  type:
    | 'input' | 'join' | 'weapon_switch' | 'reload' | 'ping'
    | 'buy' | 'buy_equipment' | 'buy_grenade'
    | 'grenade_throw' | 'bomb_plant' | 'bomb_defuse' | 'use'
    | 'webrtc_offer' | 'webrtc_answer' | 'webrtc_ice'
    | 'spectate_next' | 'spectate_prev' | 'team_select'
    | 'chat' | 'radio'
    | 'vote_kick' | 'vote_kick_response' | 'vote_kick_abstain'
    | 'surrender_start' | 'surrender_vote'
    | 'timeout'
    | 'practice_command';
  data: any;
}

const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY_MS = 1000;
const PING_INTERVAL_MS = 2000;
const WEBRTC_SETUP_TIMEOUT_MS = 3000;

const DEFAULT_STUN_URLS = ['stun:stun.l.google.com:19302', 'stun:stun1.l.google.com:19302'];

interface IceServerEnv {
  VITE_STUN_URLS?: string;
  VITE_TURN_URLS?: string;
  VITE_TURN_USERNAME?: string;
  VITE_TURN_CREDENTIAL?: string;
}

export function buildIceServers(env?: IceServerEnv): RTCIceServer[] {
  const source = env ?? (import.meta.env as unknown as IceServerEnv);
  const stunUrls = splitEnvList(source.VITE_STUN_URLS);
  const turnUrls = splitEnvList(source.VITE_TURN_URLS);
  const iceServers: RTCIceServer[] = [
    { urls: stunUrls.length > 0 ? stunUrls : DEFAULT_STUN_URLS },
  ];

  if (turnUrls.length > 0) {
    const turnServer: RTCIceServer = { urls: turnUrls };
    const username = source.VITE_TURN_USERNAME?.trim();
    const credential = source.VITE_TURN_CREDENTIAL?.trim();
    if (username && credential) {
      turnServer.username = username;
      turnServer.credential = credential;
    }
    iceServers.push(turnServer);
  }

  return iceServers;
}

function splitEnvList(raw: string | undefined): string[] {
  if (!raw) return [];
  return raw.split(',').map((value) => value.trim()).filter(Boolean);
}

export type TransportType = 'webrtc' | 'websocket';

// ============================================================
// Connection quality tracking
// ============================================================

export interface ConnectionQuality {
  rtt: number;
  packetLoss: number;
  /**
   * U9: short-window snapshot stutter (0..1). Distinguishes brief server choke
   * (e.g. transient encode pause, long tick) from sustained packet loss.
   */
  choke: number;
  transportType: TransportType;
}

/** Track snapshot arrival to compute packet loss. */
class PacketLossTracker {
  // Timestamps of received snapshots, kept in a circular buffer so both
  // recordReceived() and getPacketLoss() pruning are amortized O(1).
  private received: number[] = [];
  private head = 0; // index of oldest valid entry
  private expectedRate = 64; // server tick rate (snapshots/sec)
  private windowMs = 2000;

  recordReceived(): void {
    this.received.push(performance.now());
  }

  /** Get packet loss percentage over the last windowMs. */
  getPacketLoss(): number {
    const now = performance.now();
    const cutoff = now - this.windowMs;

    // Advance head past expired entries without re-indexing the whole array.
    while (this.head < this.received.length && this.received[this.head] < cutoff) {
      this.head++;
    }
    // Compact occasionally so the array doesn't grow unboundedly.
    if (this.head > 256 && this.head > this.received.length / 2) {
      this.received = this.received.slice(this.head);
      this.head = 0;
    }

    const expected = this.expectedRate * (this.windowMs / 1000);
    if (expected <= 0) return 0;

    const received = this.received.length - this.head;
    const loss = Math.max(0, 1 - received / expected);
    return Math.min(1, loss);
  }

  /**
   * U9: Get short-window choke percentage (last 500ms). Choke surfaces brief
   * stutter (server skipped a tick, encode burst, link microstall) that
   * doesn't move the 2-second loss number. 0..1.
   */
  getChoke(): number {
    const chokeWindowMs = 500;
    const now = performance.now();
    const cutoff = now - chokeWindowMs;
    let count = 0;
    // Linear scan over the recent tail is cheap because the tail is small at
    // 64 Hz × 0.5s ≈ 32 entries.
    for (let i = this.received.length - 1; i >= this.head; i--) {
      if (this.received[i] < cutoff) break;
      count++;
    }
    const expected = this.expectedRate * (chokeWindowMs / 1000);
    if (expected <= 0) return 0;
    return Math.min(1, Math.max(0, 1 - count / expected));
  }

  setExpectedRate(rate: number): void {
    this.expectedRate = rate;
  }
}

// ============================================================
// Connection class
// ============================================================

export class Connection {
  private ws: WebSocket | null = null;
  private readonly url: string;

  private _connected = false;
  private reconnectAttempts = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private pingTimer: ReturnType<typeof setInterval> | null = null;
  private pendingPingSentAt: number[] = [];
  private _ping = 0;

  /** Queue of raw JSON strings to send once reconnected. */
  private sendQueue: string[] = [];

  // --- WebRTC ---
  private pc: RTCPeerConnection | null = null;
  private dataChannel: RTCDataChannel | null = null;
  private _transportType: TransportType = 'websocket';
  private rtcSetupTimer: ReturnType<typeof setTimeout> | null = null;

  // --- Quality tracking ---
  private packetLossTracker = new PacketLossTracker();

  /** External callbacks */
  public onMessage: ((msg: ServerMessage) => void) | null = null;
  public onBinaryMessage: ((data: ArrayBuffer) => void) | null = null;
  public onDisconnect: (() => void) | null = null;
  public onReconnect: (() => void) | null = null;

  private matchId: string | null = null;
  private gameMode: string | null = null;
  private mapId: string | null = null;

  /** Optional GameClock reference for ping/pong clock sync. */
  private clock: GameClock | null = null;

  constructor(url: string) {
    this.url = url;
  }

  // --- Auth token helpers ---

  /** Store an auth token for use in connections. */
  setAuthToken(token: string): void {
    saveToken(token);
  }

  /** Retrieve the currently stored auth token (null if absent). */
  getAuthToken(): string | null {
    return getToken();
  }

  /** Clear the stored auth token. */
  clearAuthToken(): void {
    clearToken();
  }

  /** Attach a GameClock for ping/pong server time sync. */
  setClock(clock: GameClock): void {
    this.clock = clock;
  }

  /** Set a match ID for the next connection. */
  setMatchId(matchId: string | null): void {
    this.matchId = matchId;
  }

  /** Get the currently set match ID. */
  getMatchId(): string | null {
    return this.matchId;
  }

  /** Set the game mode for the next connection. */
  setGameMode(mode: string | null): void {
    this.gameMode = mode;
  }

  /** Get the currently set game mode. */
  getGameMode(): string | null {
    return this.gameMode;
  }

  /** Set the map ID for the next connection. */
  setMapId(mapId: string | null): void {
    this.mapId = mapId;
  }

  /** Get the currently set map ID. */
  getMapId(): string | null {
    return this.mapId;
  }

  /** Build the WebSocket URL, appending auth token, match_id, mode, and map as query params if present. */
  private buildWsUrl(): string {
    let url = this.url;
    const params: string[] = [];
    const token = getToken();
    if (token) {
      params.push(`token=${encodeURIComponent(token)}`);
    }
    if (this.matchId) {
      params.push(`match_id=${encodeURIComponent(this.matchId)}`);
    }
    if (this.gameMode) {
      params.push(`mode=${encodeURIComponent(this.gameMode)}`);
    }
    if (this.mapId) {
      params.push(`map=${encodeURIComponent(this.mapId)}`);
    }
    if (params.length > 0) {
      const sep = url.includes('?') ? '&' : '?';
      url += sep + params.join('&');
    }
    return url;
  }

  // --- Public API ---

  connect(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this.cleanup();

      try {
        this.ws = new WebSocket(this.buildWsUrl());
        this.ws.binaryType = 'arraybuffer';
      } catch (err) {
        reject(err);
        return;
      }

      this.ws.onopen = () => {
        const wasReconnect = this.reconnectAttempts > 0;
        this._connected = true;
        this.reconnectAttempts = 0;
        this.startPing();
        if (wasReconnect) {
          this.onReconnect?.();
        }
        this.flushQueue();
        // WebRTC upgrade is deferred until after the join/welcome handshake
        // (triggered by handleRawMessage when 'welcome' is received)
        resolve();
      };

      this.ws.onerror = (_ev: Event) => {
        if (!this._connected) {
          reject(new Error('WebSocket connection failed'));
        }
      };

      this.ws.onclose = () => {
        const wasConnected = this._connected;
        this._connected = false;
        this.stopPing();
        this.cleanupWebRTC();

        if (wasConnected) {
          // Drop reliable gameplay traffic accumulated while the socket was down.
          // The reconnect path should start from a fresh join instead of replaying
          // stale input/buy/chat messages against a new server-side session state.
          this.sendQueue.length = 0;
          this.onDisconnect?.();
          this.scheduleReconnect();
        }
      };

      this.ws.onmessage = (ev: MessageEvent) => {
        if (ev.data instanceof ArrayBuffer) {
          // Packet loss tracked by game layer after decode — don't double-count here
          this.onBinaryMessage?.(ev.data);
        } else {
          this.handleRawMessage(ev.data as string);
        }
      };
    });
  }

  disconnect(): void {
    this.reconnectAttempts = MAX_RECONNECT_ATTEMPTS; // prevent reconnect
    this.pendingPingSentAt.length = 0;
    this.cleanup();
    this.onDisconnect?.();
  }

  /** Send a reliable message (JSON) — always goes over WebSocket. */
  send(msg: ClientMessage): void {
    // Attach auth token to join messages so the server can verify identity
    if (msg.type === 'join') {
      const token = getToken();
      if (token) {
        msg = { ...msg, data: { ...msg.data, token } };
      }
    }
    const raw = JSON.stringify(msg);
    if (this._connected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(raw);
    } else {
      this.sendQueue.push(raw);
    }
  }

  /**
   * Send a pre-encoded string (from messages.ts encode helpers).
   * Reliable messages go over WebSocket. Queues if not connected.
   */
  sendRaw(raw: string): void {
    if (this._connected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(raw);
    } else {
      this.sendQueue.push(raw);
    }
  }

  /**
   * Send binary data (e.g. FlatBuffer encoded input).
   * Uses DataChannel if available (unreliable/fast), falls back to WebSocket.
   */
  sendBinary(data: ArrayBuffer): void {
    if (this.dataChannel?.readyState === 'open') {
      this.dataChannel.send(data);
    } else if (this._connected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
    // Binary data is not queued — it's time-sensitive game input
  }

  /**
   * Send game state data. Prefers unreliable DataChannel for low latency.
   * Falls back to WebSocket.
   */
  sendGameData(data: ArrayBuffer | string): void {
    if (this.dataChannel?.readyState === 'open') {
      if (typeof data === 'string') {
        this.dataChannel.send(data);
      } else {
        this.dataChannel.send(data);
      }
    } else if (this._connected && this.ws?.readyState === WebSocket.OPEN) {
      this.ws.send(data);
    }
  }

  isConnected(): boolean {
    return this._connected;
  }

  /** Latest round-trip time in milliseconds. */
  getPing(): number {
    return this._ping;
  }

  /** Get the current transport type being used for game data. */
  getTransportType(): TransportType {
    return this._transportType;
  }

  /** Get connection quality info for HUD display. */
  getConnectionQuality(): ConnectionQuality {
    return {
      rtt: this._ping,
      packetLoss: this.packetLossTracker.getPacketLoss(),
      choke: this.packetLossTracker.getChoke(),
      transportType: this._transportType,
    };
  }

  /** Record a snapshot received (for packet loss tracking). */
  recordSnapshotReceived(): void {
    this.packetLossTracker.recordReceived();
  }

  /** Set expected snapshot rate for packet loss calculation. */
  setExpectedSnapshotRate(rate: number): void {
    this.packetLossTracker.setExpectedRate(rate);
  }

  // ============================================================
  // WebRTC DataChannel setup
  // ============================================================

  private attemptWebRTCUpgrade(): void {
    // Check for browser support
    if (typeof RTCPeerConnection === 'undefined') {
      console.log('[Connection] WebRTC not supported, staying on WebSocket');
      return;
    }

    try {
      this.pc = new RTCPeerConnection({ iceServers: buildIceServers() });

      // The server creates the DataChannel and sends us an offer.
      // Listen for the server-created DataChannel.
      this.pc.ondatachannel = (ev) => {
        this.dataChannel = ev.channel;
        this.dataChannel.binaryType = 'arraybuffer';

        this.dataChannel.onopen = () => {
          console.log('[Connection] WebRTC DataChannel opened');
          this._transportType = 'webrtc';
          if (this.rtcSetupTimer !== null) {
            clearTimeout(this.rtcSetupTimer);
            this.rtcSetupTimer = null;
          }
        };

        this.dataChannel.onclose = () => {
          console.log('[Connection] WebRTC DataChannel closed, falling back to WebSocket');
          this._transportType = 'websocket';
        };

        this.dataChannel.onerror = (ev) => {
          console.warn('[Connection] WebRTC DataChannel error:', ev);
          this._transportType = 'websocket';
        };

        this.dataChannel.onmessage = (ev: MessageEvent) => {
          if (ev.data instanceof ArrayBuffer) {
            // Packet loss tracked by game layer after decode — don't double-count here
            this.onBinaryMessage?.(ev.data);
          } else {
            this.handleRawMessage(ev.data as string);
          }
        };
      };

      // Clear setup timer if PeerConnection enters Failed or Closed state
      this.pc.onconnectionstatechange = () => {
        const state = this.pc?.connectionState;
        if (state === 'failed' || state === 'closed') {
          if (this.rtcSetupTimer !== null) {
            clearTimeout(this.rtcSetupTimer);
            this.rtcSetupTimer = null;
          }
        }
      };

      // ICE candidate gathering
      this.pc.onicecandidate = (ev) => {
        if (ev.candidate && this.ws?.readyState === WebSocket.OPEN) {
          try {
            this.ws.send(JSON.stringify({
              type: 'webrtc_ice',
              data: { candidate: ev.candidate.toJSON() },
            }));
          } catch {
            // WebSocket may have closed between readyState check and send — ignore
          }
        }
      };

      // Timeout: if DataChannel doesn't open within 3 seconds, give up
      this.rtcSetupTimer = setTimeout(() => {
        if (this._transportType !== 'webrtc') {
          console.log('[Connection] WebRTC setup timed out, staying on WebSocket');
          this.cleanupWebRTC();
        }
        this.rtcSetupTimer = null;
      }, WEBRTC_SETUP_TIMEOUT_MS);

    } catch (err) {
      console.warn('[Connection] WebRTC setup failed:', err);
      this.cleanupWebRTC();
    }
  }

  /** Handle WebRTC signaling messages from server via WebSocket. */
  private handleRTCSignaling(msg: ServerMessage): void {
    if (!this.pc) return;

    if (msg.type === 'webrtc_offer') {
      // Server sent an offer — set remote description and create answer
      const sdpData = msg.data.sdp;
      if (!sdpData) return;
      const offer = new RTCSessionDescription({
        type: sdpData.type,
        sdp: sdpData.sdp,
      });
      this.pc.setRemoteDescription(offer).then(() => {
        return this.pc!.createAnswer();
      }).then((answer) => {
        return this.pc!.setLocalDescription(answer).then(() => {
          if (this.ws?.readyState === WebSocket.OPEN) {
            this.ws.send(JSON.stringify({
              type: 'webrtc_answer',
              data: { sdp: { sdp: answer.sdp, type: answer.type } },
            }));
          }
        });
      }).catch((err) => {
        console.warn('[Connection] Failed to handle server offer:', err);
      });
    } else if (msg.type === 'webrtc_ice') {
      const candidate = new RTCIceCandidate(msg.data.candidate);
      this.pc.addIceCandidate(candidate).catch((err) => {
        console.warn('[Connection] Failed to add ICE candidate:', err);
      });
    }
  }

  /** Get the RTCPeerConnection for voice chat audio track attachment. */
  getPeerConnection(): RTCPeerConnection | null {
    return this.pc;
  }

  private cleanupWebRTC(): void {
    if (this.rtcSetupTimer !== null) {
      clearTimeout(this.rtcSetupTimer);
      this.rtcSetupTimer = null;
    }

    if (this.dataChannel) {
      this.dataChannel.onopen = null;
      this.dataChannel.onclose = null;
      this.dataChannel.onerror = null;
      this.dataChannel.onmessage = null;
      try { this.dataChannel.close(); } catch { /* ignore */ }
      this.dataChannel = null;
    }

    if (this.pc) {
      this.pc.onicecandidate = null;
      try { this.pc.close(); } catch { /* ignore */ }
      this.pc = null;
    }

    this._transportType = 'websocket';
  }

  // --- Internal ---

  private handleRawMessage(raw: string): void {
    let msg: ServerMessage;
    try {
      msg = JSON.parse(raw) as ServerMessage;
    } catch {
      console.warn('[Connection] Failed to parse server message');
      return;
    }

    // Handle internal pong
    if (msg.type === 'pong') {
      const now = performance.now();
      const sentAt = this.pendingPingSentAt.shift();
      if (sentAt !== undefined) {
        this._ping = now - sentAt;
      }
      // Forward pong to GameClock for server time sync if wired + payload has seq/serverTime
      if (this.clock && msg.data) {
        const seq = typeof msg.data.sequence === 'number' ? msg.data.sequence : undefined;
        const serverTime = typeof msg.data.serverTime === 'number' ? msg.data.serverTime : undefined;
        if (seq !== undefined && serverTime !== undefined) {
          this.clock.receivePong(seq, serverTime);
        }
      }
      return;
    }

    // After server welcome, initiate WebRTC upgrade
    if (msg.type === 'welcome') {
      this.attemptWebRTCUpgrade();
    }

    // Handle WebRTC signaling
    if (msg.type === 'webrtc_offer' || msg.type === 'webrtc_answer' || msg.type === 'webrtc_ice') {
      this.handleRTCSignaling(msg);
      return;
    }

    this.onMessage?.(msg);
  }

  private flushQueue(): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
    for (const raw of this.sendQueue) {
      this.ws.send(raw);
    }
    this.sendQueue.length = 0;
  }

  private startPing(): void {
    this.stopPing();
    this.pingTimer = setInterval(() => {
      if (this._connected && this.ws?.readyState === WebSocket.OPEN) {
        const sentAt = performance.now();
        this.pendingPingSentAt.push(sentAt);
        if (this.pendingPingSentAt.length > 8) {
          this.pendingPingSentAt.shift();
        }
        // If a GameClock is wired and wants to ping, include its tracking payload
        // so the server can echo back sequence/serverTime for clock sync.
        let payload: Record<string, unknown> = {};
        if (this.clock && this.clock.shouldPing()) {
          const p = this.clock.sendPing();
          payload = { sequence: p.sequence, clientTime: p.timestamp };
        }
        this.ws.send(JSON.stringify({ type: 'ping', data: payload }));
      }
    }, PING_INTERVAL_MS);
  }

  private stopPing(): void {
    if (this.pingTimer !== null) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  private scheduleReconnect(): void {
    if (this.reconnectAttempts >= MAX_RECONNECT_ATTEMPTS) {
      console.warn('[Connection] Max reconnect attempts reached');
      return;
    }

    const delay = BASE_RECONNECT_DELAY_MS * Math.pow(2, this.reconnectAttempts);
    this.reconnectAttempts++;

    console.log(
      `[Connection] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${MAX_RECONNECT_ATTEMPTS})`,
    );

    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect().catch(() => {
        if (!this._connected) {
          this.scheduleReconnect();
        }
      });
    }, delay);
  }

  private cleanup(): void {
    this.stopPing();
    this.pendingPingSentAt.length = 0;
    this.cleanupWebRTC();

    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.ws) {
      // Remove handlers before closing to prevent triggering reconnect
      this.ws.onopen = null;
      this.ws.onclose = null;
      this.ws.onerror = null;
      this.ws.onmessage = null;

      if (
        this.ws.readyState === WebSocket.OPEN ||
        this.ws.readyState === WebSocket.CONNECTING
      ) {
        this.ws.close();
      }
      this.ws = null;
    }

    this._connected = false;
  }
}
